import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useDemo } from "@/contexts/DemoContext";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Auto-transitions confirmed bookings with start_date <= now to "ready".
 * Runs on mount and every 5 minutes. In demo mode, operates on the store.
 */
export function useAutoReady() {
  const { profile } = useAuth();
  const { isDemo } = useDemo();
  const qc = useQueryClient();
  const running = useRef(false);

  useEffect(() => {
    // In demo mode, auto-ready is not needed (mock data already has correct statuses)
    if (isDemo || !profile?.business_id) return;

    const run = async () => {
      if (running.current) return;
      running.current = true;
      try {
        const now = new Date().toISOString();
        const { data, error } = await supabase
          .from("bookings")
          .update({ status: "ready" as any, updated_at: now })
          .eq("status", "confirmed")
          .lte("start_date", now)
          .eq("business_id", profile.business_id)
          .select("id");

        if (!error && data && data.length > 0) {
          qc.invalidateQueries({ queryKey: ["bookings"] });
          qc.invalidateQueries({ queryKey: ["vehicles"] });
          qc.invalidateQueries({ queryKey: ["dashboard"] });
        }
      } catch {
        // silent
      } finally {
        running.current = false;
      }
    };

    run();
    const interval = setInterval(run, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isDemo, profile?.business_id, qc]);
}
